import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from "@/screens/auth/layout.tsx";
import SignInScreen from "@/screens/auth/signin/LoginScreen.tsx";
import {
    DEFAULT_ROUTE,
    EMAIL_SENT_ROUTE, EXPLORE_APPS_ROUTE,
    FORGOT_PASSWORD_ROUTE,
    LOGIN_ROUTE,
    NOT_FOUND_ROUTE,
    OTP_ROUTE,
    PAYMENTS_ROUTE, PROJECT_LAYOUT_ROUTE, PROJECTS_ROUTE,
    REQUIREMENT_GATHERING_ROUTE,
    RESET_PASSWORD_ROUTE,
    SIGNUP_ROUTE,
    UNIVERSAL_APP_BUILDER_ROUTE, RESTRICTED_ROUTE,
    ADMIN_USER_LIST_PATH,
    ADMIN_PROJECT_PATH
} from "@/constants/RoutesConfig.tsx";
import SignupComponent from "@/screens/auth/signup/SignupComponent.tsx";
import CommonService from "@/services/common.service.ts";
import { useAppDispatch, useAppSelector } from "@/store/hooks.ts";
import ForgotPasswordScreen from "@/screens/auth/forgot-password/page.tsx";
import OtpScreen from "@/screens/auth/otp/page.tsx";
import ResetEmailSuccessComponent from "@/screens/auth/reset-email-success/ResetEmailSuccessComponent.tsx";
import ResetPasswordScreen from "@/screens/auth/reset-password/page.tsx";
import PaymentsComponent from "@/screens/app/payments/PaymentsComponent.tsx";
import NotFoundScreenComponent from '@/components/common-components/not-found-screen/NotFoundScreenComponent';
import NoInternetComponent from "@/components/common-components/no-internet/NoInternetComponent.tsx";
import AppLayout from '@/screens/app/layout';
import EnhancedRequirementGatheringComponent from '@/screens/app/project/enhanced-requirement-gathering/EnhancedRequirementGatheringComponent';
import ProjectComponent from '@/screens/app/project/ProjectComponent';
import ProjectsComponent from "@/screens/app/projects-list/ProjectsComponent.tsx";
import ExploreAppsComponent from "@/screens/app/explore-apps/ExploreAppsComponent.tsx";
import UniversalAppBuilderLayoutComponent
    from "@/screens/app/project/universal-app-builder-layout/UniversalAppBuilderLayoutComponent.tsx";
import SupabaseConnectionComponent
    from "@/screens/app/project/universal-app-builder-layout/tools/supabase/supabase-connection/SupabaseConnectionComponent.tsx";
import UnAuthHeaderComponent from "@/components/common-components/un-auth-header/UnAuthHeaderComponent.tsx";
import { useSearchParams } from "react-router-dom";
import { setCurrentPrompt } from "@/store/actions/auth.action.ts";
import RestrictedRouteComponent from "@/components/common-components/restricted-route/RestrictedRouteComponent.tsx";
import UserListScreen from "@/screens/admin/user-list/UserListScreen.tsx";


const ProtectedRoute = (props: React.PropsWithChildren<any>) => {
    const { children } = props;
    const { token } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const storedPrompt = searchParams.get('storedPrompt');

        if (storedPrompt) {
            // Save to Redux
            dispatch(setCurrentPrompt(storedPrompt));

            // Remove from URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('storedPrompt');
            setSearchParams(newSearchParams);

            return; // Wait for prompt to be set before continuing with auth logic
        }

        // Only do token check after prompt logic has run (or wasn't needed)
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate, searchParams, setSearchParams, dispatch]);

    return (
        <NoInternetComponent>
            {children}
        </NoInternetComponent>
    );
};


const UnProtectedRoute = (props: React.PropsWithChildren<any>) => {

    const { children } = props;
    const { token } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();
    const location = useLocation();


    useEffect(() => {
        let returnUrl = DEFAULT_ROUTE;
        if (token) {
            const query = CommonService.parseQueryString(location.search);
            if (Object.keys(query).includes('returnUrl')) {
                returnUrl = query.returnUrl;
            }
            navigate(returnUrl);
        }
    }, [token, navigate, location])


    return (
        <NoInternetComponent>
            {children}
        </NoInternetComponent>
    );
}

const Navigator = () => {


    const { isAdmin, originalAdminState } = useAppSelector((state) => state.auth);


    return (
        <NoInternetComponent>
            <Routes>
                <Route path={'/supabase/oauth2/callback'} element={<SupabaseConnectionComponent />} />

                <Route element={<AppLayout />}>
                    <Route index element={
                        <ProtectedRoute>
                            {(isAdmin && !originalAdminState) ? <Navigate to={ADMIN_USER_LIST_PATH} /> : <ProjectsComponent />}
                        </ProtectedRoute>
                    } />
                    <Route path={PROJECTS_ROUTE} element={
                        <ProtectedRoute>
                            {(isAdmin && !originalAdminState) ? <Navigate to={ADMIN_USER_LIST_PATH} /> : <ProjectsComponent />}
                        </ProtectedRoute>
                    } />
                    <Route path={ADMIN_USER_LIST_PATH} element={
                        <ProtectedRoute>
                            <UserListScreen />
                        </ProtectedRoute>
                    } />
                    <Route path={ADMIN_PROJECT_PATH} element={
                        <ProtectedRoute>
                            <UserListScreen />
                        </ProtectedRoute>
                    } />
                </Route>
                <Route path={PROJECT_LAYOUT_ROUTE} element={<ProjectComponent />}>
                    <Route path={REQUIREMENT_GATHERING_ROUTE + "/:projectId"} element={
                        <ProtectedRoute>
                            <EnhancedRequirementGatheringComponent />
                        </ProtectedRoute>
                    } />
                </Route>

                <Route path={UNIVERSAL_APP_BUILDER_ROUTE}
                    element={
                        <ProtectedRoute>
                            <UniversalAppBuilderLayoutComponent />
                        </ProtectedRoute>
                    } />

                <Route element={<AuthLayout />}>
                    <Route index element={<UnProtectedRoute><SignInScreen /></UnProtectedRoute>} />
                    <Route path={LOGIN_ROUTE} element={<UnProtectedRoute><SignInScreen /></UnProtectedRoute>} />
                    <Route path={SIGNUP_ROUTE} element={<UnProtectedRoute><SignupComponent /></UnProtectedRoute>} />
                    <Route path={OTP_ROUTE} element={<UnProtectedRoute><OtpScreen /></UnProtectedRoute>} />
                    <Route path={RESET_PASSWORD_ROUTE}
                        element={<UnProtectedRoute><ResetPasswordScreen /></UnProtectedRoute>} />
                    <Route path={FORGOT_PASSWORD_ROUTE}
                        element={<UnProtectedRoute><ForgotPasswordScreen /></UnProtectedRoute>} />
                    <Route path={EMAIL_SENT_ROUTE}
                        element={<UnProtectedRoute><ResetEmailSuccessComponent /></UnProtectedRoute>} />
                </Route>

                <Route element={<UnAuthHeaderComponent />}>
                    <Route path={NOT_FOUND_ROUTE} element={<NotFoundScreenComponent />} />
                    <Route path={PAYMENTS_ROUTE} element={<PaymentsComponent />} />
                    <Route path={RESTRICTED_ROUTE} element={<RestrictedRouteComponent />} />
                    <Route path={EXPLORE_APPS_ROUTE} element={<ExploreAppsComponent />} />
                </Route>
                <Route path={"*"} element={<Navigate to={NOT_FOUND_ROUTE} />} />
            </Routes>
        </NoInternetComponent>
    );
}
export default Navigator;

